import { useCallback, useEffect, useRef } from "react";

/**
 * @typedef {object} FnpChatUser
 * @property {string} [userId]
 * @property {string} [name]
 * @property {string} [email]
 * @property {string} [phone]
 * @property {string} [externalLoginKey] sensitive session detail for bot lookups; never shown to agents
 * @property {string} [sid] sensitive session detail for bot lookups; never shown to agents
 * @property {string} [subOrderId] the sub-order the customer is asking about
 */

/**
 * @typedef {object} UseFnpChatOptions
 * @property {string} [botId]
 * @property {string} [botVersionId]
 * @property {"local" | "uat" | "prod"} [env] which chat backend the client talks to
 * @property {FnpChatUser | null} [user] the signed-in customer, or null for a guest
 * @property {Record<string, unknown>} [config] theme, branding, texts, layout (read when the chat first opens)
 * @property {(event: { type: string }) => void} [onEvent] chat events for analytics
 * @property {string} clientUrl where the hosted chat client is served from
 */

// calls made before widget.js has loaded wait in this queue
/** */
const queue = () => (window.FnpChat ??= Object.assign((...args) => void window.FnpChat?.q?.push(args), { q: [] }));

/**
 * Loads the hosted chat client's widget.js once and keeps the customer in sync.
 * Nothing is shown until open() is called.
 * @param {UseFnpChatOptions} options
 * @returns {{ open: () => void, close: () => void }}
 */
export const useFnpChat = ({ botId, botVersionId, env = "prod", user, config, onEvent, clientUrl }) => {
  const scriptUrl = `${clientUrl}/widget.js`;

  useEffect(() => {
    window.FnpChatConfig = config;
    queue();
    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.dataset.env = env;
    if (botId) script.dataset.botId = botId;
    if (botVersionId) script.dataset.botVersionId = botVersionId;
    document.body.appendChild(script);
    return () => {
      window.FnpChat?.("destroy");
      delete window.FnpChat;
      script.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptUrl, env, botId, botVersionId]);

  const userJson = JSON.stringify(user ?? null);
  useEffect(() => {
    window.FnpChatUser = user ?? null;
    if (window.FnpChat?.loaded) window.FnpChat("setUser", user ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userJson]);

  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);
  useEffect(() => {
    queue()("on", (event) => onEventRef.current?.(event));
  }, [scriptUrl, env, botId, botVersionId]);

  return {
    open: useCallback(() => queue()("open"), []),
    close: useCallback(() => queue()("close"), []),
  };
};
